import classNames from 'classnames/bind';
import Button, { ButtonVariant } from 'src/components/ui-kit/button/button';
import Icon, { IconTheme, IconVariant } from 'src/components/ui-kit/icon/icon';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './pagination.module.scss';
import ReactPaginate from 'react-js-pagination';
import FormSelect from 'src/components/ui-kit/form/_select';
import FormTextInputRange from 'src/components/ui-kit/form/_text-input__range';
import buttonStyles from 'src/components/ui-kit/button/button.module.scss';
import { configuration } from 'src/configurations/configuration';
import { useRouter } from 'next/router';
import useSelectOptionsByArray from 'src/hooks/use-select-options-by-array';

const classesContext = classNames.bind(styles);

export default function PaginationControls({
  currentPage,
  perPage,
  total,
  getPageUrl,
  setPerPage
}: {
  currentPage: number;
  perPage: number;
  total: number;
  getPageUrl: (page: number) => string;
  setPerPage: (page: number) => void;
}): JSX.Element {
  const { t } = useTranslation(['components:ui-kit:pagination']);

  const router = useRouter();

  const [goToPageValue, setGoToPageValue] = useState(1);

  const handleChangePage = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;

    setGoToPageValue(Number(value));
  };

  const handleChangePerPage = (e: ChangeEvent<HTMLSelectElement>): void => {
    const value = e.target.value;

    setPerPage(Number(value));
  };

  const goToPage = (page: number): void => {
    router.push(getPageUrl(page), undefined, { shallow: true });
  };

  const handleGoToPage = (): void => {
    goToPage(goToPageValue);
  };

  const handlePress = (event: React.KeyboardEvent): void => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      handleGoToPage();
    }
  };

  useEffect(() => {
    setGoToPageValue(1);
  }, [perPage]);

  const totalPage = Math.ceil(total / perPage);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPage || !totalPage;
  const perPageOptions = useSelectOptionsByArray(configuration.pagination.allowedPerPage, false);

  return (
    <div className={styles.pagination}>
      <ReactPaginate
        activePage={currentPage}
        itemsCountPerPage={perPage}
        totalItemsCount={total}
        pageRangeDisplayed={5}
        onChange={goToPage}
        getPageUrl={getPageUrl}
        prevPageText={<Icon
          variant={IconVariant.ARROW_LEFT}
          theme={IconTheme.PRIMARY} />}
        nextPageText={<Icon
          variant={IconVariant.ARROW}
          theme={IconTheme.PRIMARY} />}
        itemClassFirst={styles.exclude}
        itemClassLast={styles.exclude}
        itemClassPrev={classesContext([{ exclude: isFirstPage }])}
        itemClassNext={classesContext([{ exclude: isLastPage }])}
        innerClass={styles.pagination_pages}
        activeClass={styles.pagination_item__active}
        itemClass={styles.pagination_item}
        linkClass={classesContext([
          'pagination_link',
          buttonStyles.button,
          buttonStyles.button__icon,
          buttonStyles.button__outline
        ])}
      />
      <div className={styles.pagination_actions}>
        <div className={styles.pagination_actions_item}>
          <label className={classesContext(['pagination_action', 'pagination_action__label'])}>
            <span className={classesContext('pagination_action__text')}>{t('TEXT_GO_TO_PAGE')}</span>
            <FormTextInputRange
              name='page'
              min={1}
              max={totalPage}
              value={String(goToPageValue)}
              onChange={handleChangePage}
              onKeyDown={handlePress}
              className={classesContext(['pagination_action', 'pagination_action__field'])}
            />
          </label>
          <Button
            type='button'
            onClick={handleGoToPage}
            variant={ButtonVariant.PRIMARY}
            className={classesContext(['pagination_action', 'pagination_action__button'])}
          >
            {t('BUTTON_GO')}
          </Button>
        </div>
        <div className={styles.pagination_actions_item}>
          <div className={classesContext(['pagination_action', 'pagination_action__label'])}>
            <label
              htmlFor='perPage'
              className={classesContext('pagination_action__text', 'pagination_action__text__desktop')}
            >
              {t('TEXT_LISTINGS_PER_PAGE')}
            </label>
            <label
              htmlFor='perPage'
              className={classesContext('pagination_action__text', 'pagination_action__text__mobile')}
            >
              {t('TEXT_PER_PAGE')}
            </label>
            <FormSelect
              id='perPage'
              value={perPage}
              name='perPage'
              onChange={handleChangePerPage}
              options={perPageOptions}
              className={classesContext(['pagination_action', 'pagination_action__select'])}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
